/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { ProductVariantNames } from 'bundles/browse-common/constants';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import NoSearchResultsMessage from 'bundles/search-common/components/NoSearchResultsMessage';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import {
  combineDegreeAndDiscoveryData,
  formatDiscoveryData,
} from 'bundles/search-common/components/withNoSearchResultsRecommendations';
import { NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import NoResultsSearchCollectionQuery from 'bundles/search-common/queries/NoResultsSearchCollectionQuery.graphql';
import NoSearchResultsPremiumProductCollections from 'bundles/search-common/queries/NoSearchResultsPremiumProductCollections.graphql';
import type { NoResultsSearchCollectionQuery as NoResultsSearchCollectionQueryData } from 'bundles/search-common/queries/__generated__/NoResultsSearchCollectionQuery';
import type { NoSearchResultsPremiumProductCollectionsQuery } from 'bundles/search-common/queries/__generated__/NoSearchResultsPremiumProductCollections';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';

import _t from 'i18n!nls/search';

type EventingData = {
  searchIndex: string;
  searchIndexPosition: number;
  page?: string | undefined;
  searchQuery?: string | undefined;
  filtersApplied?: Record<string, string[]> | undefined;
};

type Props = {
  eventingData: EventingData;
};
const SearchNoResultsRecommendations = ({ eventingData }: Props) => {
  const showNoResultsSearch = GrowthDiscoveryEpicClient.get('showUpdatedNoResultsPage');
  const { data: newDiscoveryCollectionsData, loading: newDiscoveryCollectionsLoading } =
    useQuery<NoResultsSearchCollectionQueryData>(NoResultsSearchCollectionQuery, {
      variables: {
        contextType: 'PAGE',
        contextId: 'new-search-zero-state',
      },
      context: { clientName: 'gatewayGql' },
      skip: !showNoResultsSearch,
    });
  const { data: discoveryCollectionsData, loading: discoveryCollectionsLoading } =
    useQuery<NoResultsSearchCollectionQueryData>(NoResultsSearchCollectionQuery, {
      variables: {
        contextType: 'PAGE',
        contextId: 'search-zero-state',
      },
      context: { clientName: 'gatewayGql' },
      skip: showNoResultsSearch,
    });
  const { data: premiumProductCollectionData, loading: premiumProductsCollectionLoading } =
    useQuery<NoSearchResultsPremiumProductCollectionsQuery>(NoSearchResultsPremiumProductCollections, {
      variables: {
        productVariants: [ProductVariantNames.BachelorsDegree, ProductVariantNames.MastersDegree],
      },
      context: {
        clientName: 'gatewayGql',
      },
      skip: showNoResultsSearch,
    });
  const discoveryCollections = discoveryCollectionsData?.DiscoveryCollections?.queryCollections;
  const premiumProductCollection = premiumProductCollectionData?.PremiumProductCollections?.queryCollection;
  const newDiscoveryCollections = newDiscoveryCollectionsData?.DiscoveryCollections?.queryCollections;
  const recommendationHits = showNoResultsSearch
    ? formatDiscoveryData(newDiscoveryCollections || [], newDiscoveryCollectionsLoading)
    : combineDegreeAndDiscoveryData({
        discoveryCollections,
        discoveryCollectionsLoading,
        premiumProductCollection,
        premiumProductsCollectionLoading,
      });
  const recommendationHitsLoading =
    discoveryCollectionsLoading || premiumProductsCollectionLoading || newDiscoveryCollectionsLoading;
  const { getSearchResults, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { query } = searchData || {};
  const isConsumerSearchPage = useIsConsumerSerp();
  const noSearchResultsCollectionData = {
    collectionId: newDiscoveryCollections?.[0]?.id ?? discoveryCollections?.[0]?.id,
    collectionName: newDiscoveryCollections?.[0]?.label ?? discoveryCollections?.[0]?.label,
    collectionIndex: 0,
  };

  return (
    <div role="region" aria-label={_t('Search Results')} id="searchResults">
      <NoSearchResultsMessage query={query} recommendationHitsLength={recommendationHits?.length} />
      <SearchCards
        hits={recommendationHits}
        resultsPerPage={NUM_NO_RESULTS_RECS_PER_PRODUCT_TYPE * 2}
        noResults={true}
        searchCardImageProps={{ fit: 'crop' }}
        eventingData={eventingData}
        isLoading={recommendationHitsLoading || isLoading}
        isSearchPage={isConsumerSearchPage}
        noSearchResultsCollectionData={noSearchResultsCollectionData}
      />
    </div>
  );
};

export default SearchNoResultsRecommendations;
